// soccer_admin/src/pages/Matches.jsx
import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { fetchAdminMatches } from '../services/api';

const Matches = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMatches, setTotalMatches] = useState(0);

  // Filters
  const [filters, setFilters] = useState({ competition: '', from: '', to: '' });
  const [showFilters, setShowFilters] = useState(false);

  const fetchMatches = async (currentPage = page) => {
    setLoading(true);
    try {
      const res = await fetchAdminMatches(currentPage, limit, filters);
      const matchesData = res.data.data || res.data;
      setMatches(matchesData);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.pages || 1);
        setTotalMatches(res.data.pagination.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatches(page);
  }, [page, filters]);

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1); // Reset to first page on filter change
  };

  const clearFilters = () => {
    setFilters({ competition: '', from: '', to: '' });
    setPage(1);
  };

  const getWinnerBadgeClass = (match) => {
    if (!match.winner_username) return 'bg-gray-600 text-white';
    return 'bg-green-600 text-white';
  };

  if (loading) {
    return <div className="text-white text-center py-10">Loading matches...</div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-white">Recent Matches</h1>
        <div className="flex gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition"
          >
            <FunnelIcon className="h-5 w-5" />
            Filters
          </button>
          <button
            onClick={() => { setPage(1); fetchMatches(1); }}
            className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg transition"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-gray-800 rounded-xl p-4 mb-6 border border-gray-700 grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-1">Competition</label>
            <select
              name="competition"
              value={filters.competition}
              onChange={handleFilterChange}
              className="w-full bg-gray-700 text-white p-2 rounded-lg border border-gray-600 focus:outline-none focus:border-yellow-500"
            >
              <option value="">All</option>
              <option value="domestic">Domestic</option>
              <option value="champions">Champions</option>
              <option value="europa">Europa</option>
              <option value="friendly">Friendly</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-1">From Date</label>
            <input
              type="date"
              name="from"
              value={filters.from}
              onChange={handleFilterChange}
              className="w-full bg-gray-700 text-white p-2 rounded-lg border border-gray-600 focus:outline-none focus:border-yellow-500"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-1">To Date</label>
            <input
              type="date"
              name="to"
              value={filters.to}
              onChange={handleFilterChange}
              className="w-full bg-gray-700 text-white p-2 rounded-lg border border-gray-600 focus:outline-none focus:border-yellow-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-gray-600 hover:bg-gray-500 text-white py-2 rounded-lg transition"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto bg-gray-800 rounded-lg">
        <table className="min-w-full text-white">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="p-3">ID</th>
              <th className="p-3">User1</th>
              <th className="p-3">User2</th>
              <th className="p-3">Score</th>
              <th className="p-3">Winner</th>
              <th className="p-3">Competition</th>
              <th className="p-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(matches) && matches.map((match) => (
              <tr key={match.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                <td className="p-3 text-white">{match.id}</td>
                <td className="p-3 text-white">{match.user1_username}</td>
                <td className="p-3 text-white">{match.user2_username}</td>
                <td className="p-3 text-white font-mono">{match.user1_score} - {match.user2_score}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getWinnerBadgeClass(match)}`}>
                    {match.winner_username || 'Draw'}
                  </span>
                </td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    match.competition === 'domestic' ? 'bg-green-600/20 text-green-400' :
                    match.competition === 'champions' ? 'bg-yellow-600/20 text-yellow-400' :
                    match.competition === 'europa' ? 'bg-blue-600/20 text-blue-400' :
                    'bg-gray-600/20 text-gray-400'
                  }`}>
                    {match.competition || 'friendly'}
                  </span>
                </td>
                <td className="p-3 text-gray-300">{new Date(match.match_date).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!Array.isArray(matches) || matches.length === 0) && (
          <div className="text-center text-gray-400 py-8">No matches found.</div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {Array.isArray(matches) && matches.map((match) => (
          <div key={match.id} className="bg-gray-800 rounded-xl p-4 border border-gray-700 shadow-md">
            <div className="flex justify-between items-start mb-2">
              <span className="text-xs text-gray-500">Match #{match.id}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getWinnerBadgeClass(match)}`}>
                {match.winner_username || 'Draw'}
              </span>
            </div>
            <div className="flex justify-between items-center my-3">
              <div className="text-center flex-1">
                <p className="text-white font-bold text-lg">{match.user1_username}</p>
                <p className="text-yellow-400 text-2xl font-bold">{match.user1_score}</p>
              </div>
              <div className="text-gray-400 text-xl font-bold px-2">VS</div>
              <div className="text-center flex-1">
                <p className="text-white font-bold text-lg">{match.user2_username}</p>
                <p className="text-yellow-400 text-2xl font-bold">{match.user2_score}</p>
              </div>
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span>{match.competition || 'friendly'}</span>
              <span>{new Date(match.match_date).toLocaleString()}</span>
            </div>
          </div>
        ))}
        {(!Array.isArray(matches) || matches.length === 0) && (
          <div className="text-center text-white py-8">No matches found.</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-6 text-white">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page === 1}
            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <span className="text-sm">
            Page {page} of {totalPages} ({totalMatches} matches)
          </span>
          <button
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages}
            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Matches;